import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const { t } = useT();
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | ok | expired | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= MAX_ATTEMPTS) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState("ok");
          checkAuth();
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
        setTimeout(poll, POLL_MS);
      } catch {
        if (!cancelled) setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, checkAuth]);

  const content = {
    checking: { icon: <Loader2 size={40} strokeWidth={1.2} className="animate-spin" />, text: t("paySuccessChecking") },
    ok: { icon: <CheckCircle2 size={40} strokeWidth={1.2} className="text-emerald-700" />, text: t("paySuccessOk") },
    expired: { icon: <XCircle size={40} strokeWidth={1.2} className="text-rose-700" />, text: t("paySuccessExpired") },
    timeout: { icon: <XCircle size={40} strokeWidth={1.2} className="text-amber-700" />, text: t("paySuccessTimeout") },
    error: { icon: <XCircle size={40} strokeWidth={1.2} className="text-rose-700" />, text: t("paySuccessError") },
  }[state];

  return (
    <div className="ed-page min-h-screen flex items-center justify-center px-8" data-testid="payment-success-page">
      <div className="ed-card p-14 max-w-lg w-full text-center">
        <div className="flex justify-center">{content.icon}</div>
        <p className="font-serif text-2xl mt-6 leading-snug" data-testid="payment-status-text">{content.text}</p>
        <div className="flex justify-center gap-3 mt-10">
          {state === "ok" || state === "checking" ? (
            <button data-testid="payment-to-app" onClick={() => navigate("/dashboard")} className="ed-btn" disabled={state === "checking"}>
              {t("paySuccessToApp")}
            </button>
          ) : (
            <button data-testid="payment-retry" onClick={() => navigate("/pricing")} className="ed-btn">
              {t("paySuccessRetry")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
